import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Button, Input, Spin, Result, Typography, message } from 'antd';
import { UploadOutlined, CheckCircleFilled, ReloadOutlined } from '@ant-design/icons';

const { Title, Text, Paragraph } = Typography;

export { PublicUpload };

// Public preview of a teacher reference attachment (no auth) via /api/upload-link/:token/file/:fileId.
function PublicAttachment({ token, att }) {
    const url = `/api/upload-link/${token}/file/${att.drive_file_id}`;
    const mime = att.mime_type || '';
    let body;
    if (mime.startsWith('audio/')) {
        body = <audio controls src={url} style={{ width: '100%' }} />;
    } else if (mime.startsWith('image/')) {
        body = (
            <a href={url} target="_blank" rel="noopener noreferrer">
                <img
                    src={url}
                    alt={att.original_name || 'Tài liệu'}
                    style={{ maxWidth: '100%', maxHeight: 240, display: 'block', borderRadius: 6 }}
                />
            </a>
        );
    } else {
        body = (
            <a href={url} target="_blank" rel="noopener noreferrer">
                {att.original_name || 'Mở tài liệu'}
            </a>
        );
    }
    return (
        <div style={{ marginBottom: 12 }}>
            <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>
                {att.original_name}
            </Text>
            {body}
        </div>
    );
}

// Fully public, single-screen mobile-first upload page. NO auth, NO Recoil, NO Layout.
function PublicUpload() {
    const { token } = useParams();

    // 'loading' | 'invalid' | 'identify' | 'confirmed' | 'done'
    const [stage, setStage] = useState('loading');
    const [context, setContext] = useState(null);   // {assignmentTitle, dueDate, className}
    const [number, setNumber] = useState('');
    const [studentName, setStudentName] = useState('');
    const [resolving, setResolving] = useState(false);
    const [uploading, setUploading] = useState(false);
    const fileInputRef = useRef(null);

    // Load assignment context on mount.
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await fetch(`/api/upload-link/${token}`, { method: 'GET' });
                if (!res.ok) throw new Error('bad');
                const json = await res.json();
                if (!cancelled) {
                    setContext(json);
                    setStage('identify');
                }
            } catch (e) {
                if (!cancelled) setStage('invalid');
            }
        })();
        return () => { cancelled = true; };
    }, [token]);

    const handleResolve = async () => {
        const n = (number || '').trim();
        if (!n) {
            message.warning('Vui lòng nhập số thứ tự');
            return;
        }
        setResolving(true);
        try {
            const res = await fetch(`/api/upload-link/${token}/resolve`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ number: n }),
            });
            const json = await res.json().catch(() => ({}));
            if (res.ok && json.studentName) {
                setStudentName(json.studentName);
                setStage('confirmed');
            } else {
                message.error(json.message || 'Không tìm thấy học sinh với số này');
            }
        } catch (e) {
            message.error('Có lỗi xảy ra, vui lòng thử lại');
        }
        setResolving(false);
    };

    const handlePickFile = () => {
        if (fileInputRef.current) fileInputRef.current.click();
    };

    const handleFileChange = async (e) => {
        const file = e.target.files && e.target.files[0];
        e.target.value = '';
        if (!file) return;
        setUploading(true);
        try {
            const formData = new FormData();
            formData.append('number', (number || '').trim());
            formData.append('file', file);
            const res = await fetch(`/api/upload-link/${token}`, {
                method: 'POST',
                body: formData,
            });
            const json = await res.json().catch(() => ({}));
            if (res.ok) {
                if (json.studentName) setStudentName(json.studentName);
                setStage('done');
            } else {
                message.error(json.message || 'Nộp bài thất bại, vui lòng thử lại');
            }
        } catch (err) {
            message.error('Nộp bài thất bại, vui lòng thử lại');
        }
        setUploading(false);
    };

    const resetForAnother = () => {
        setStage('confirmed');
    };

    const wrapStyle = {
        maxWidth: 480,
        margin: '0 auto',
        padding: '24px 16px',
        minHeight: '100vh',
        boxSizing: 'border-box',
    };

    if (stage === 'loading') {
        return (
            <div style={{ ...wrapStyle, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Spin size="large" />
            </div>
        );
    }

    if (stage === 'invalid') {
        return (
            <div style={wrapStyle}>
                <Result
                    status="warning"
                    title="Link không hợp lệ hoặc đã hết hạn"
                    subTitle="Vui lòng liên hệ giáo viên để nhận link mới."
                />
            </div>
        );
    }

    return (
        <div style={wrapStyle}>
            {/* Header / context */}
            <div style={{ textAlign: 'center', marginBottom: 24 }}>
                <img
                    src="/logos/MASON_logo-ngang.png"
                    alt="The Mason"
                    style={{ height: 48, marginBottom: 16 }}
                    onError={(e) => { e.target.style.display = 'none'; }}
                />
                <Title level={4} style={{ marginBottom: 4 }}>{context?.assignmentTitle}</Title>
                {context?.className &&
                    <Text type="secondary" style={{ display: 'block' }}>Lớp: {context.className}</Text>}
                {context?.dueDate &&
                    <Text type="secondary" style={{ display: 'block' }}>
                        Hạn nộp: {new Date(context.dueDate).toLocaleString('vi-VN')}
                    </Text>}
            </div>

            {/* Description + teacher reference materials — shown above the upload step.
                Lets a parent read the prompt / hear the listening audio before recording. */}
            {context?.description &&
                <Paragraph style={{ whiteSpace: 'pre-wrap', marginBottom: 16 }}>
                    {context.description}
                </Paragraph>}

            {context?.attachments && context.attachments.length > 0 &&
                <div
                    style={{
                        marginBottom: 24,
                        padding: '12px 14px',
                        background: 'var(--bg-subtle)',
                        borderRadius: 8,
                        border: '1px solid var(--line)',
                    }}
                >
                    <Text strong style={{ display: 'block', marginBottom: 8 }}>
                        Tài liệu của giáo viên
                    </Text>
                    {context.attachments.map((att, i) => (
                        <PublicAttachment key={att.drive_file_id || i} token={token} att={att} />
                    ))}
                </div>}

            {/* Step 1 — identify */}
            {stage === 'identify' &&
                <div>
                    <Paragraph>Nhập số thứ tự của bạn để nộp bài:</Paragraph>
                    <Input.Group compact style={{ display: 'flex', marginBottom: 16 }}>
                        <Button style={{ pointerEvents: 'none', fontWeight: 700 }}>ME</Button>
                        <Input
                            size="large"
                            inputMode="numeric"
                            pattern="[0-9]*"
                            placeholder="Số thứ tự"
                            value={number}
                            onChange={(e) => setNumber(e.target.value.replace(/[^0-9]/g, ''))}
                            onPressEnter={handleResolve}
                            style={{ flex: 1 }}
                        />
                    </Input.Group>
                    <Button
                        type="primary"
                        size="large"
                        block
                        loading={resolving}
                        onClick={handleResolve}
                    >
                        Tiếp tục
                    </Button>
                </div>}

            {/* Step 2 — confirmed, ready to upload */}
            {stage === 'confirmed' &&
                <div style={{ textAlign: 'center' }}>
                    <Paragraph style={{ fontSize: 16 }}>
                        Nộp bài cho: <Text strong>{studentName}</Text>
                    </Paragraph>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*,application/pdf,audio/*"
                        capture="environment"
                        style={{ display: 'none' }}
                        onChange={handleFileChange}
                    />
                    <Button
                        type="primary"
                        size="large"
                        block
                        icon={<UploadOutlined />}
                        loading={uploading}
                        onClick={handlePickFile}
                        style={{ height: 64, fontSize: 18, marginTop: 16 }}
                    >
                        Chọn tệp & nộp bài
                    </Button>
                    <Text type="secondary" style={{ display: 'block', marginTop: 12, fontSize: 12 }}>
                        Hỗ trợ ảnh, PDF hoặc ghi âm.
                    </Text>
                    <Button
                        type="link"
                        style={{ marginTop: 8 }}
                        onClick={() => { setStudentName(''); setNumber(''); setStage('identify'); }}
                    >
                        Không phải bạn? Nhập lại số khác
                    </Button>
                </div>}

            {/* Step 3 — done */}
            {stage === 'done' &&
                <Result
                    icon={<CheckCircleFilled style={{ color: '#52c41a' }} />}
                    status="success"
                    title="Nộp bài thành công!"
                    subTitle={studentName
                        ? `Đã nhận bài nộp của ${studentName}. Nộp lại sẽ thay thế tệp cũ.`
                        : 'Đã nhận bài nộp. Nộp lại sẽ thay thế tệp cũ.'}
                    extra={
                        <Button type="primary" icon={<ReloadOutlined />} onClick={resetForAnother}>
                            Nộp lại (thay thế)
                        </Button>
                    }
                />}
        </div>
    );
}
